import React from 'react'
import PropTypes from 'prop-types'
import styled, { css, ThemeContext } from 'styled-components'
import breakpoint from 'tools/styled-components-breakpoint'
import { pixelise } from 'tools/style'
import { borderRadius, borderRadiusSizes, colors } from 'theme/default'
import Div from 'components/Div'
import Flex from 'components/Flex'
import Card from 'components/Card'
import IconClose from 'components/Icons/IconClose'
import IconBack from 'components/Icons/IconBack'

export const headerHeight = '60px'
export const footerHeight = '64px'

const containerStyle = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  overflow: hidden;
  ${props => props.maxWidth ? `max-width: ${pixelise(props.maxWidth)};` : ''}
  ${breakpoint('mobile')`
    border-radius: 0;
    max-height: 100%;
    height: 100%;
    width: 100%;
  `}
  ${breakpoint('desktop')`
    border-radius: ${borderRadius[borderRadiusSizes.medium]};
    height: auto;
    max-height: 90vh;
    width: ${props => pixelise(props.width) || '500px'};
  `}
`

export const Container = styled(Div)`
  ${containerStyle}
`
Container.propTypes = {
  ...Div.propTypes,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}
Container.defaultProps = {
  ...Div.defaultProps
}

export const FlexContainer = styled(Flex)`
  ${containerStyle}
`
FlexContainer.propTypes = {
  ...Flex.propTypes,
  ...Container.propTypes
}
FlexContainer.defaultProps = {
  ...Flex.defaultProps,
  ...Container.defaultProps,
  alignItems: 'stretch',
  flexDirection: 'column'
}

const PrevButtonContainer = styled(Flex)`
  position: absolute;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  cursor: pointer;
  padding: 10px;
`

export const PrevButton = ({ onClick, className, fill, ...props }) => {
  const theme = React.useContext(ThemeContext)
  return (
    <PrevButtonContainer data-testid='button.return' className={className} onClick={onClick} {...props}>
      <IconBack fill={fill || theme.colorHeadingsFont} iconSize='medium' />
    </PrevButtonContainer>
  )
}

PrevButton.propTypes = {
  onClick: PropTypes.func,
  fill: PropTypes.string
}

const CloseButtonContainer = styled(Flex)`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  padding: 10px;
`

export const CloseButton = ({ onClick, fill, className }) => {
  const theme = React.useContext(ThemeContext)
  return (
    <CloseButtonContainer data-testid='button.close' className={className} onClick={onClick}>
      <IconClose fill={fill || theme.colorHeadingsFont} iconSize='medium' />
    </CloseButtonContainer>
  )
}

CloseButton.propTypes = {
  onClick: PropTypes.func,
  fill: PropTypes.string
}

export const ModalHeader = styled(Flex).attrs((props) => ({
  boxShadow: props.boxShadow || 'heavy',
  justifyContent: props.justifyContent || 'center',
  alignItems: props.alignItems || 'center',
  flexShrink: 0
}))`
  height: ${headerHeight};
  background-color: ${props => props.theme.colorContainerColor};
  position: relative;
  z-index: 1;
`
ModalHeader.defaultProps = {
  theme: {
    colorContainerColor: colors.container
  }
}

export const getBodyMaxHeight = ({ withoutHeader, withoutFooter }) => {
  const headerSubstraction = withoutHeader ? '0px' : headerHeight
  const footerSubtraction = withoutFooter ? '0px' : footerHeight
  return `calc(90vh - ${headerSubstraction} - ${footerSubtraction})`
}

export const ModalBody = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'center',
  flexGrow: 1,
  flexShrink: 1
}))`
  background-color: ${props => props.theme.colorBackgroundColor};
  overflow: auto;
  height: 100%;
  ${breakpoint('desktop')`
    height: auto;
    /* IE requires height to be able to correctly calculate remaining height */
    max-height: ${getBodyMaxHeight};
  `}
`
ModalBody.defaultProps = {
  theme: {
    colorBackgroundColor: colors.background
  }
}

export const ModalFooter = styled(Flex).attrs(() => ({
  boxShadow: 'heavy',
  paddingLeft: 'medium',
  paddingRight: 'medium',
  paddingTop: 'small',
  paddingBottom: 'small',
  flexShrink: 0
}))`
  height: ${footerHeight};
  background: ${props => props.theme.colorContainerColor};
  z-index: 1;
`
ModalFooter.defaultProps = {
  theme: {
    colorContainerColor: colors.container
  }
}

export const ModalSection = styled(Card).attrs(() => ({
  padding: 'medium',
  marginTop: 'small',
  borderRadius: borderRadiusSizes.none
}))`
  align-self: stretch;
  flex-direction: column;
  align-items: stretch;
  flex-shrink: 0;
  box-shadow: none;
`
